import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { tap, catchError, map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { settings } from '../interface/user';
import { ResponseData } from '../interface/response';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly COOKIE_KEY = environment.production
    ? 'mconnect_settings'
    : 'dev_mconnect_settings';

  private readonly COOKIE_TTL_MINUTES = 60;

  private settingsBS = new BehaviorSubject<settings | null>(null);
  public settings$ = this.settingsBS.asObservable();

  constructor(
    private http: HttpClient,
    private cookie: CookieService,
  ) {}

  /* =======================
     PUBLIC API
     ======================= */

  init(): Observable<settings> {
    const cached = this.getFromCookie();

    if (cached) {
      this.settingsBS.next(cached);
      return of(cached);
    }

    return this.fetchFromApi();
  }

  get snapshot(): settings | null {
    return this.settingsBS.value;
  }

  refresh(): Observable<settings> {
    return this.fetchFromApi(true);
  }

  clear(): void {
    this.cookie.delete(this.COOKIE_KEY, '/');
    this.settingsBS.next(null);
  }

  /* =======================
     INTERNAL
     ======================= */

  private fetchFromApi(force = false): Observable<settings> {
    return this.http.get<ResponseData>(`/getSiteDetail`).pipe(
      map((res) => res?.result as settings),
      tap((settings) => {
        const mapped = this.mapSettings(settings);
        this.saveToCookie(mapped);
        this.settingsBS.next(mapped);
      }),
      catchError((err) => {
        return of(this.settingsBS.value as settings);
      }),
    );
  }

  private saveToCookie(data: settings): void {
    const expiry = new Date();
    expiry.setMinutes(expiry.getMinutes() + this.COOKIE_TTL_MINUTES);

    const payload = {
      expiresAt: expiry.getTime(),
      data,
    };

    this.cookie.set(this.COOKIE_KEY, JSON.stringify(payload), expiry, '/');
  }

  private getFromCookie(): settings | null {
    if (!this.cookie.check(this.COOKIE_KEY)) return null;

    try {
      const raw = JSON.parse(this.cookie.get(this.COOKIE_KEY));
      if (Date.now() > raw.expiresAt) {
        this.clear();
        return null;
      }
      return raw.data;
    } catch {
      this.clear();
      return null;
    }
  }

  mapSettings(data: settings | any): settings {
    return {
      ...data,
      siteLogo: data.siteLogo_path || '',
      siteName: data.siteName || '',
      siteTagLine: data.siteTagLine || '',
      siteWebsite: data.siteWebsite || '',
      siteShortName: data.siteShortName || '',
      siteDateFormat: data.siteDateFormat || '',
      themeColor: data.themeColor || '#417adf',
    };
  }
}
